import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./buyland.css";
import {
  ArrowLeft,
  Menu,
  X,
  ExternalLink,
  FileCheck,
  Landmark,
  ShieldCheck,
  BookOpen,
  AlertTriangle,
  SearchCheck,
  Scale,
  Phone,
  MessageCircle,
  Mail,
  Heart,
  Grid2X2,
  List,
  ChevronDown,
  RotateCcw,
  MapPin,
  BadgeCheck,
  ArrowUpRight,
  CircleCheck,
} from "lucide-react";

function BuyLand() {
  const navigate = useNavigate();

  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =================================================
     FILTER STATES
  ================================================= */

  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [approvalType, setApprovalType] = useState("");
  const [loanAvailable, setLoanAvailable] = useState("");
  const [sort, setSort] = useState("latest");

  /* =================================================
     UI STATES
  ================================================= */

  const [showConsult, setShowConsult] = useState(false);
  const [consultUser, setConsultUser] = useState(null);

  const [showLandMenu, setShowLandMenu] = useState(false);

  const [viewMode, setViewMode] = useState("grid");

  const [favorites, setFavorites] = useState([]);

  const [activeApproval, setActiveApproval] = useState("all");

  /* =================================================
     USER
  ================================================= */

  const user = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("civilink_user")
      );
    } catch {
      return null;
    }
  }, []);

  /* =================================================
     FETCH LANDS
  ================================================= */

  const fetchLands = async (customFilters = {}) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      const currentSearch =
        customFilters.search !== undefined
          ? customFilters.search
          : search;

      const currentLocation =
        customFilters.location !== undefined
          ? customFilters.location
          : location;

      const currentMinPrice =
        customFilters.minPrice !== undefined
          ? customFilters.minPrice
          : minPrice;

      const currentMaxPrice =
        customFilters.maxPrice !== undefined
          ? customFilters.maxPrice
          : maxPrice;

      const currentApproval =
        customFilters.approvalType !== undefined
          ? customFilters.approvalType
          : approvalType;

      const currentLoan =
        customFilters.loanAvailable !== undefined
          ? customFilters.loanAvailable
          : loanAvailable;

      const currentSort =
        customFilters.sort !== undefined
          ? customFilters.sort
          : sort;

      if (currentSearch) {
        params.append("q", currentSearch);
      }

      if (currentLocation) {
        params.append("location", currentLocation);
      }

      if (currentMinPrice) {
        params.append("minPrice", currentMinPrice);
      }

      if (currentMaxPrice) {
        params.append("maxPrice", currentMaxPrice);
      }

      if (currentApproval) {
        params.append(
          "approvalType",
          currentApproval
        );
      }

      if (currentLoan) {
        params.append(
          "loanAvailable",
          currentLoan
        );
      }

      if (currentSort) {
        params.append("sort", currentSort);
      }

      const res = await fetch(
        `/api/land/buy?${params.toString()}`
      );

      if (!res.ok) {
        throw new Error(
          "Failed to fetch land listings"
        );
      }

      const data = await res.json();

      if (data.success) {
        setLands(data.lands || []);
      } else {
        setLands([]);
      }
    } catch (error) {
      console.error(
        "Buy land fetch error:",
        error
      );

      setLands([]);
    } finally {
      setLoading(false);
    }
  };

  /* =================================================
     INITIAL FETCH
  ================================================= */

  useEffect(() => {
    fetchLands();
  }, []);

  /* =================================================
     CONTACT SELLER
  ================================================= */

  const contactSeller = (seller) => {
    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }

    setConsultUser(seller);
    setShowConsult(true);
  };

  /* =================================================
     FAVORITES
  ================================================= */

  const toggleFavorite = (landId) => {
    setFavorites((prev) =>
      prev.includes(landId)
        ? prev.filter((id) => id !== landId)
        : [...prev, landId]
    );
  };

  /* =================================================
     QUICK APPROVAL FILTER
  ================================================= */

  const applyApprovalChip = (type) => {
    setActiveApproval(type);

    let newApproval = "";

    if (type === "dtcp") {
      newApproval = "DTCP";
    }

    if (type === "cmda") {
      newApproval = "CMDA";
    }

    if (type === "panchayat") {
      newApproval = "Panchayat";
    }

    setApprovalType(newApproval);

    fetchLands({
      approvalType: newApproval,
    });
  };

  /* =================================================
     RESET FILTERS
  ================================================= */

  const resetFilters = () => {
    setSearch("");
    setLocation("");
    setMinPrice("");
    setMaxPrice("");
    setApprovalType("");
    setLoanAvailable("");
    setSort("latest");
    setActiveApproval("all");

    fetchLands({
      search: "",
      location: "",
      minPrice: "",
      maxPrice: "",
      approvalType: "",
      loanAvailable: "",
      sort: "latest",
    });
  };

  /* =================================================
     FORMAT PRICE
  ================================================= */

  const formatPrice = (price) => {
    const value = Number(price);

    if (!value) {
      return "Price on request";
    }

    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    }

    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }

    return `₹${value.toLocaleString("en-IN")}`;
  };

  const formatFullPrice = (price) => {
    const value = Number(price);

    if (!value) {
      return "";
    }

    return `₹${value.toLocaleString("en-IN")}`;
  };

  /* =================================================
     APPROVAL LABEL
  ================================================= */

  const getApprovalLabel = (land) => {
    if (
      land.approvalType &&
      String(land.approvalType).trim()
    ) {
      return land.approvalType;
    }

    return "Land Listing";
  };

  /* =================================================
     LAND MENU
  ================================================= */

  const renderLandGuide = () => {
    if (!showLandMenu) {
      return null;
    }

    return (
      <div
        className="land-menu-overlay"
        onClick={() => setShowLandMenu(false)}
      >
        <aside
          className="land-menu-panel"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}

          <div className="land-menu-header">
            <div>
              <span className="land-menu-eyebrow">
                CIVILINK LAND GUIDE
              </span>

              <h2>
                Before You Buy Land
              </h2>

              <p>
                Useful official portals, verification
                guidance and property information for
                land buyers.
              </p>
            </div>

            <button
              className="land-menu-close"
              onClick={() =>
                setShowLandMenu(false)
              }
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* =================================================
              OFFICIAL GOVERNMENT PORTALS
          ================================================= */}

          <div className="land-menu-section">
            <div className="land-section-title">
              <Landmark size={17} />
              <span>
                Official Government Portals
              </span>
            </div>

            <a
              href="https://tcp.tn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="government-link"
            >
              <div className="government-link-icon">
                <Landmark size={19} />
              </div>

              <div>
                <strong>
                  DTCP Tamil Nadu
                </strong>

                <span>
                  Town & Country Planning
                </span>
              </div>

              <ExternalLink size={16} />
            </a>

            <a
              href="https://rera.tn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="government-link"
            >
              <div className="government-link-icon">
                <ShieldCheck size={19} />
              </div>

              <div>
                <strong>
                  TNRERA
                </strong>

                <span>
                  Check applicable project and
                  agent registration information
                </span>
              </div>

              <ExternalLink size={16} />
            </a>

            <a
              href="https://www.tnreginet.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="government-link"
            >
              <div className="government-link-icon">
                <FileCheck size={19} />
              </div>

              <div>
                <strong>
                  TNREGINET
                </strong>

                <span>
                  Registration and EC-related
                  services
                </span>
              </div>

              <ExternalLink size={16} />
            </a>

            <a
              href="https://eservices.tn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="government-link"
            >
              <div className="government-link-icon">
                <FileCheck size={19} />
              </div>

              <div>
                <strong>
                  Tamil Nadu Land Records
                </strong>

                <span>
                  Patta and land-record services
                </span>
              </div>

              <ExternalLink size={16} />
            </a>

            <a
              href="https://onlineppa.tn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="government-link"
            >
              <div className="government-link-icon">
                <Scale size={19} />
              </div>

              <div>
                <strong>
                  Online Planning Permission
                </strong>

                <span>
                  Planning permission services
                </span>
              </div>

              <ExternalLink size={16} />
            </a>

          </div>

          {/* =================================================
              WHAT TO VERIFY
          ================================================= */}

          <div className="land-menu-section">

            <div className="land-section-title">
              <SearchCheck size={17} />
              <span>
                What You Should Verify
              </span>
            </div>

            <div className="verification-card">

              <div className="verification-number">
                01
              </div>

              <div>
                <strong>
                  Ownership & Title
                </strong>

                <p>
                  Verify the seller's ownership,
                  title documents and chain of title
                  with qualified professionals.
                </p>
              </div>

            </div>

            <div className="verification-card">

              <div className="verification-number">
                02
              </div>

              <div>
                <strong>
                  Patta & Land Records
                </strong>

                <p>
                  Check relevant revenue records,
                  survey details and land information
                  through official services.
                </p>
              </div>

            </div>

            <div className="verification-card">

              <div className="verification-number">
                03
              </div>

              <div>
                <strong>
                  Encumbrance Certificate
                </strong>

                <p>
                  Check registered transactions or
                  encumbrances relevant to the property.
                </p>
              </div>

            </div>

            <div className="verification-card">

              <div className="verification-number">
                04
              </div>

              <div>
                <strong>
                  Layout / Planning Approval
                </strong>

                <p>
                  Verify applicable DTCP, CMDA or
                  local planning approval independently.
                </p>
              </div>

            </div>

            <div className="verification-card">

              <div className="verification-number">
                05
              </div>

              <div>
                <strong>
                  RERA Registration
                </strong>

                <p>
                  Where applicable, verify the
                  project's TNRERA registration and
                  details independently.
                </p>
              </div>

            </div>

            <div className="verification-card">

              <div className="verification-number">
                06
              </div>

              <div>
                <strong>
                  Land Classification
                </strong>

                <p>
                  Verify the applicable land-use
                  classification and permissions before
                  purchasing.
                </p>
              </div>

            </div>

          </div>

          {/* =================================================
              2026 PROPERTY INFORMATION
          ================================================= */}

          <div className="land-menu-section">

            <div className="land-section-title">
              <BookOpen size={17} />
              <span>
                2026 Property Updates
              </span>
            </div>

            <div className="rule-card">

              <span className="rule-year">
                2026
              </span>

              <div>
                <strong>
                  Development & Building Rules
                </strong>

                <p>
                  Tamil Nadu notified amendments
                  during 2026 to development and
                  building-related rules. Verify the
                  latest requirements applicable to
                  the specific property.
                </p>
              </div>

            </div>

            <div className="rule-card">

              <span className="rule-year">
                2026
              </span>

              <div>
                <strong>
                  Land-use Requirements
                </strong>

                <p>
                  2026 notifications introduced
                  changes relating to agricultural and
                  non-agricultural land use in certain
                  non-planning areas. Check the current
                  classification and applicable permission.
                </p>
              </div>

            </div>

            <div className="rule-card">

              <span className="rule-year">
                2026
              </span>

              <div>
                <strong>
                  Electronic Registration
                </strong>

                <p>
                  2026 notifications address electronic
                  presentation of instruments for certain
                  first-sale transactions involving plots
                  and flats. Verify the current procedure
                  with the registration authority.
                </p>
              </div>

            </div>

          </div>

          {/* =================================================
              SIMPLE BUYER CHECKLIST
          ================================================= */}

          <div className="land-menu-section">

            <div className="land-section-title">
              <CircleCheck size={17} />
              <span>
                Before Paying Any Advance
              </span>
            </div>

            <div className="buyer-checklist">

              <div>
                <CircleCheck size={15} />
                <span>
                  Verify seller identity
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Verify ownership and title
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Check survey number and boundaries
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Check Patta / relevant land records
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Check EC and registration records
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Verify applicable layout approval
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Verify RERA where applicable
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Consult a qualified property lawyer
                </span>
              </div>

            </div>

          </div>

          {/* =================================================
              DISCLAIMER
          ================================================= */}

          <div className="land-disclaimer">

            <AlertTriangle size={21} />

            <div>

              <strong>
                Important: Civilink is a listing
                platform
              </strong>

              <p>
                Civilink provides property listings
                and communication facilities only.
                A listing on Civilink does not mean
                that the property has been legally
                verified, approved, or guaranteed by
                Civilink.
              </p>

              <p>
                Buyers must independently verify
                ownership, title, Patta, survey details,
                encumbrances, approvals, land
                classification, RERA status where
                applicable, and other legal requirements
                with the relevant authorities and
                qualified professionals before making
                any payment or entering into a transaction.
              </p>

              <p>
                Government portals linked here are
                external websites. Civilink does not
                control their content, availability,
                decisions or records.
              </p>

            </div>

          </div>

          {/* FOOTER */}

          <div className="land-menu-footer">

            <ShieldCheck size={15} />

            <span>
              Always verify property information
              through the appropriate official authority.
            </span>

          </div>

        </aside>
      </div>
    );
  };

  /* =================================================
     MAIN UI
  ================================================= */

  return (
    <div className="buyhouse-page buyland-page">

      {/* =================================================
          TOP NAVIGATION
      ================================================= */}

      <div className="buyland-topbar">

        <button
          className="buyland-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />

          <span>
            Back
          </span>
        </button>

        <button
          className="land-guide-btn"
          onClick={() =>
            setShowLandMenu(true)
          }
        >
          <Menu size={18} />

          <span>
            Land Buying Guide
          </span>
        </button>

      </div>


      {/* =================================================
          HERO
      ================================================= */}

      <section className="buyland-hero">

        <div className="buyland-hero-overlay" />

        <div className="buyland-hero-content">

          <div className="buyland-badge">
            <Landmark size={15} />

            Land Marketplace
          </div>

          <h1>
            Find the right
            <br />

            <span>
              piece of land.
            </span>
          </h1>

          <p>
            Explore land listings from sellers
            and layouts across Tamil Nadu.
            Verify the property independently
            before making any transaction.
          </p>

          <div className="buyland-trust-row">

            <div>
              <FileCheck size={17} />

              Check Documents
            </div>

            <div>
              <SearchCheck size={17} />

              Verify Approval
            </div>

            <div>
              <ShieldCheck size={17} />

              Check RERA
            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          SEARCH / FILTER
      ================================================= */}

      <section className="buyland-search-wrapper">

        <div className="buyland-search-card">

          {/* SEARCH */}

          <div className="land-search-field">

            <SearchCheck size={19} />

            <div>

              <label>
                Search
              </label>

              <input
                placeholder="Land, layout or keyword"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchLands();
                  }
                }}
              />

            </div>

          </div>


          {/* LOCATION */}

          <div className="land-search-field">

            <MapPin size={19} />

            <div>

              <label>
                Location
              </label>

              <input
                placeholder="City or area"
                value={location}
                onChange={(e) =>
                  setLocation(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchLands();
                  }
                }}
              />

            </div>

          </div>


          {/* MIN PRICE */}

          <div className="land-search-field">

            <span className="land-rupee">
              ₹
            </span>

            <div>

              <label>
                Min Price
              </label>

              <input
                type="number"
                placeholder="Any"
                value={minPrice}
                onChange={(e) =>
                  setMinPrice(e.target.value)
                }
              />

            </div>

          </div>


          {/* MAX PRICE */}

          <div className="land-search-field">

            <span className="land-rupee">
              ₹
            </span>

            <div>

              <label>
                Max Price
              </label>

              <input
                type="number"
                placeholder="Any"
                value={maxPrice}
                onChange={(e) =>
                  setMaxPrice(e.target.value)
                }
              />

            </div>

          </div>


          {/* APPROVAL */}

          <select
            className="land-select"
            value={approvalType}
            onChange={(e) => {
              setApprovalType(e.target.value);

              setActiveApproval(
                e.target.value
                  ? e.target.value.toLowerCase()
                  : "all"
              );
            }}
          >
            <option value="">
              All Approvals
            </option>

            <option value="DTCP">
              DTCP
            </option>

            <option value="CMDA">
              CMDA
            </option>

            <option value="Panchayat">
              Panchayat
            </option>
          </select>


          {/* LOAN */}

          <select
            className="land-select"
            value={loanAvailable}
            onChange={(e) =>
              setLoanAvailable(e.target.value)
            }
          >
            <option value="">
              Loan Status
            </option>

            <option value="yes">
              Loan Available
            </option>

            <option value="no">
              No Loan
            </option>
          </select>


          {/* SORT */}

          <select
            className="land-select"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);

              fetchLands({
                sort: e.target.value,
              });
            }}
          >
            <option value="latest">
              Latest
            </option>

            <option value="priceLow">
              Price: Low → High
            </option>

            <option value="priceHigh">
              Price: High → Low
            </option>
          </select>


          {/* SEARCH */}

          <button
            className="land-search-btn"
            onClick={() => fetchLands()}
          >
            <SearchCheck size={18} />

            Search
          </button>


          {/* RESET */}

          <button
            className="land-reset-btn"
            onClick={resetFilters}
          >
            <RotateCcw size={15} />

            Reset
          </button>

        </div>

      </section>


      {/* =================================================
          QUICK APPROVAL FILTERS
      ================================================= */}

      <section className="quick-filter-section">

        <div className="quick-filters">

          <button
            className={
              activeApproval === "all"
                ? "active"
                : ""
            }
            onClick={() =>
              applyApprovalChip("all")
            }
          >
            All Land
          </button>

          <button
            className={
              activeApproval === "dtcp"
                ? "active"
                : ""
            }
            onClick={() =>
              applyApprovalChip("dtcp")
            }
          >
            DTCP
          </button>

          <button
            className={
              activeApproval === "cmda"
                ? "active"
                : ""
            }
            onClick={() =>
              applyApprovalChip("cmda")
            }
          >
            CMDA
          </button>

          <button
            className={
              activeApproval === "panchayat"
                ? "active"
                : ""
            }
            onClick={() =>
              applyApprovalChip("panchayat")
            }
          >
            Panchayat
          </button>

          <button
            className="more-filter-chip"
            onClick={() =>
              setShowLandMenu(true)
            }
          >
            <BookOpen size={16} />

            Buying Guide
          </button>

        </div>

      </section>


      {/* =================================================
          RESULTS HEADER
      ================================================= */}

      <section className="buyhouse-results-header">

        <div>

          <p className="results-label">
            LAND MARKETPLACE
          </p>

          <h2>
            {loading
              ? "Finding land..."
              : `${lands.length} Land Listings`}
          </h2>

        </div>


        <div className="results-controls">

          {/* VIEW TOGGLE */}

          <div className="view-toggle">

            <button
              className={
                viewMode === "grid"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode("grid")
              }
              aria-label="Grid view"
            >
              <Grid2X2 size={18} />
            </button>

            <button
              className={
                viewMode === "list"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode("list")
              }
              aria-label="List view"
            >
              <List size={19} />
            </button>

          </div>


          {/* SORT */}

          <div className="sort-wrapper">

            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);

                fetchLands({
                  sort: e.target.value,
                });
              }}
            >
              <option value="latest">
                Latest First
              </option>

              <option value="priceLow">
                Price: Low → High
              </option>

              <option value="priceHigh">
                Price: High → Low
              </option>
            </select>

            <ChevronDown size={16} />

          </div>

        </div>

      </section>


      {/* =================================================
          LAND LIST
      ================================================= */}

      <main
        className={
          viewMode === "grid"
            ? "buyhouse-list"
            : "buyhouse-list list-view"
        }
      >

        {loading ? (

          <div className="buyhouse-loading-state">

            <div className="loading-spinner" />

            <h3>
              Finding available land...
            </h3>

            <p>
              We're searching through available
              property listings.
            </p>

          </div>

        ) : lands.length === 0 ? (

          <div className="buyhouse-empty-state">

            <div className="empty-icon">
              <SearchCheck size={30} />
            </div>

            <h3>
              No land found
            </h3>

            <p>
              Try changing your location,
              approval type or price range.
            </p>

            <button onClick={resetFilters}>
              <RotateCcw size={16} />

              Reset Filters
            </button>

          </div>

        ) : (

          lands.map((l, index) => (

            <article
              key={l._id}
              className="buyhouse-card"
            >

              {/* =================================================
                  IMAGE
              ================================================= */}

              <div className="house-image-wrapper">

                {l.layoutImageUrl ? (

                  <img
                    src={l.layoutImageUrl}
                    alt={
                      l.title ||
                      "Land property"
                    }
                    className="buyhouse-image"
                    loading="lazy"
                  />

                ) : (

                  <div className="house-image-placeholder">

                    <span>
                      🌳
                    </span>

                    <p>
                      Land Image
                    </p>

                  </div>

                )}

                <div className="image-gradient" />


                {/* APPROVAL BADGE */}

                <span
                  className={
                    index % 3 === 1
                      ? "property-badge featured"
                      : "property-badge"
                  }
                >
                  {getApprovalLabel(l)}
                </span>


                {/* FAVORITE */}

                <button
                  className={`favorite-btn ${
                    favorites.includes(l._id)
                      ? "liked"
                      : ""
                  }`}
                  onClick={() =>
                    toggleFavorite(l._id)
                  }
                  aria-label="Save land"
                >
                  <Heart
                    size={19}
                    fill={
                      favorites.includes(l._id)
                        ? "currentColor"
                        : "none"
                    }
                  />
                </button>

              </div>


              {/* =================================================
                  CARD BODY
              ================================================= */}

              <div className="buyhouse-info">

                <div className="house-title-row">

                  <div>

                    <h3>
                      {l.title ||
                        "Land Property"}
                    </h3>

                    <p className="house-location">

                      <MapPin size={15} />

                      {l.location?.city ||
                        "Location"}

                      {l.location?.state
                        ? `, ${l.location.state}`
                        : ""}

                    </p>

                  </div>

                  <ArrowUpRight
                    className="card-arrow"
                    size={20}
                  />

                </div>


                {/* PRICE */}

                <div className="house-price-row">

                  <div>

                    <strong>
                      {formatPrice(l.price)}
                    </strong>

                    <span>
                      {formatFullPrice(l.price)}
                    </span>

                  </div>

                </div>


                {/* LAND INFORMATION */}

                <div className="land-card-tags">

                  {l.approvalType && (

                    <span>
                      <FileCheck size={13} />

                      {l.approvalType}
                    </span>

                  )}

                  {l.loanAvailable && (

                    <span>
                      <ShieldCheck size={13} />

                      {String(
                        l.loanAvailable
                      ).toLowerCase() ===
                      "yes"
                        ? "Loan Available"
                        : "Loan Info"}
                    </span>

                  )}

                </div>


                {/* SELLER */}

                <div className="seller-row">

                  <div className="seller-profile">

                    <div className="seller-avatar">

                      {l.postedBy?.photoURL ? (

                        <img
                          src={
                            l.postedBy.photoURL
                          }
                          alt={
                            l.postedBy.name ||
                            "Seller"
                          }
                        />

                      ) : (

                        <span>
                          {(
                            l.postedBy?.name ||
                            "S"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </span>

                      )}

                    </div>


                    <div>

                      <strong>
                        {l.postedBy?.name ||
                          "Property Seller"}
                      </strong>

                      <span>
                        <BadgeCheck size={13} />

                        Verified Seller
                      </span>

                    </div>

                  </div>


                  <button
                    className="contact-btn"
                    onClick={() =>
                      contactSeller(
                        l.postedBy
                      )
                    }
                  >
                    Contact
                  </button>

                </div>

              </div>

            </article>

          ))

        )}

      </main>


      {/* =================================================
          TRUST STRIP
      ================================================= */}

      <section className="trust-strip">

        <div className="trust-item">

          <div className="trust-icon">
            <FileCheck size={22} />
          </div>

          <div>

            <strong>
              Verify Documents
            </strong>

            <span>
              Check before purchasing
            </span>

          </div>

        </div>


        <div className="trust-item">

          <div className="trust-icon">
            <Landmark size={22} />
          </div>

          <div>

            <strong>
              Official Portals
            </strong>

            <span>
              Access government services
            </span>

          </div>

        </div>


        <div className="trust-item">

          <div className="trust-icon">
            <ShieldCheck size={22} />
          </div>

          <div>

            <strong>
              Verify Approval
            </strong>

            <span>
              DTCP / CMDA / applicable approvals
            </span>

          </div>

        </div>


        <div className="trust-item">

          <div className="trust-icon">
            <Scale size={22} />
          </div>

          <div>

            <strong>
              Buy Carefully
            </strong>

            <span>
              Consult qualified professionals
            </span>

          </div>

        </div>

      </section>


      {/* =================================================
          LAND BUYING GUIDE
      ================================================= */}

      {renderLandGuide()}


      {/* =================================================
          CONTACT SELLER
      ================================================= */}

      {showConsult && (

        <div
          className="customer-sheet-overlay"
          onClick={() =>
            setShowConsult(false)
          }
        >

          <div
            className="customer-sheet"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="sheet-top">

              <div className="sheet-handle" />

              <button
                className="sheet-close"
                onClick={() =>
                  setShowConsult(false)
                }
                aria-label="Close contact"
              >
                <X size={20} />
              </button>

            </div>


            <p className="sheet-eyebrow">
              CIVILINK CONTACT
            </p>


            <h3 className="sheet-title">
              Contact Seller
            </h3>


            <p className="sheet-subtitle">
              Choose how you'd like to
              connect.
            </p>


            {/* SELLER PROFILE */}

            <div className="consult-profile">

              <div className="consult-avatar">

                {consultUser?.photoURL ? (

                  <img
                    src={
                      consultUser.photoURL
                    }
                    alt={
                      consultUser.name ||
                      "Seller"
                    }
                  />

                ) : (

                  <span>
                    {(
                      consultUser?.name ||
                      "S"
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                )}

              </div>


              <div>

                <h4>
                  {consultUser?.name ||
                    "Property Seller"}
                </h4>

                <p>
                  <BadgeCheck size={14} />

                  Verified Seller
                </p>

              </div>

            </div>


            {/* CONTACT OPTIONS */}

            <div className="contact-actions">

              {consultUser?.phone && (

                <>

                  {/* CALL */}

                  <button
                    className="contact-action"
                    onClick={() => {

                      window.location.href =
                        `tel:${consultUser.phone}`;

                      setShowConsult(false);
                    }}
                  >

                    <div className="contact-action-icon phone">
                      <Phone size={20} />
                    </div>

                    <div>

                      <strong>
                        Call Seller
                      </strong>

                      <span>
                        {consultUser.phone}
                      </span>

                    </div>

                    <ArrowUpRight size={18} />

                  </button>


                  {/* WHATSAPP */}

                  <button
                    className="contact-action"
                    onClick={() => {

                      const text =
                        encodeURIComponent(
                          `Hello ${
                            consultUser.name ||
                            ""
                          },

I found your land listing on Civilink and would like to discuss the property with you.`
                        );

                      const cleanPhone =
                        String(
                          consultUser.phone
                        ).replace(
                          /\D/g,
                          ""
                        );

                      const whatsappNumber =
                        cleanPhone.startsWith(
                          "91"
                        )
                          ? cleanPhone
                          : `91${cleanPhone}`;

                      window.open(
                        `https://wa.me/${whatsappNumber}?text=${text}`,
                        "_blank"
                      );

                      setShowConsult(false);
                    }}
                  >

                    <div className="contact-action-icon whatsapp">
                      <MessageCircle size={20} />
                    </div>

                    <div>

                      <strong>
                        WhatsApp
                      </strong>

                      <span>
                        Chat directly with seller
                      </span>

                    </div>

                    <ArrowUpRight size={18} />

                  </button>

                </>

              )}


              {/* EMAIL */}

              {consultUser?.email && (

                <button
                  className="contact-action"
                  onClick={() => {

                    window.location.href =
                      `mailto:${consultUser.email}?subject=Land Inquiry from Civilink`;

                    setShowConsult(false);
                  }}
                >

                  <div className="contact-action-icon email">
                    <Mail size={20} />
                  </div>

                  <div>

                    <strong>
                      Email Seller
                    </strong>

                    <span>
                      {consultUser.email}
                    </span>

                  </div>

                  <ArrowUpRight size={18} />

                </button>

              )}

            </div>


            {/* NO CONTACT */}

            {!consultUser?.phone &&
              !consultUser?.email && (

                <div className="contact-unavailable">

                  Contact information is not
                  available.

                </div>

              )}


            {/* CANCEL */}

            <button
              className="sheet-cancel-btn"
              onClick={() =>
                setShowConsult(false)
              }
            >
              Cancel
            </button>

          </div>

        </div>

      )}

    </div>
  );
}

export default BuyLand;